import { Anchor, Flex, ScrollArea, Stack, Text } from '@mantine/core';
import { Link, useSearchParams } from 'react-router-dom';
import CustomSpinner from '@/components/Spinner.tsx';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import { useAutomation } from '@/context/AutomationContext/useAutomationContext.ts';
import AutomationPageWrapper from '@/layouts/AutomationPageWrapper/AutomationPageWrapper.tsx';
import CompositionBrowser from '@/pages/Editor/components/Composition/CompositionBrowser.tsx';

/**
 * Expert release work has a stable destination so it does not compete with
 * the ordinary Editor publishing journey.
 */
export default function AutomationReleasePage() {
  const automation = useAutomation();
  const { data: latest } = useRetrieveLatestAutomationVersion();
  const [searchParams] = useSearchParams();
  const requestedVersionId = searchParams.get('version');
  const requestedReleaseId = searchParams.get('release');
  const guided = searchParams.get('flow') === 'guided' && !requestedReleaseId;
  const rootVersionId = requestedVersionId || latest?.id;

  if (!rootVersionId) return <CustomSpinner />;

  return (
    <AutomationPageWrapper automation={automation}>
      <ScrollArea w="100%" h="100%">
        <Flex justify="center" w="100%">
          <Stack w="min(100%, 1040px)" p="xl" gap="md">
            <Stack gap={4}>
              <Anchor component={Link} to={`/automations/${automation.id}/editor`} w="fit-content">
                Back to editor
              </Anchor>
              <Text fw={600} size="xl">
                {guided ? 'Choose child implementations' : 'Release Builder'}
              </Text>
              <Text size="sm" c="dimmed">
                {guided
                  ? 'Select the exact versions for this automation’s logical children. You can review the complete Release after saving your selections.'
                  : 'Use this full-page workspace for coordinated releases, explicit dependencies, or release-specific evidence. Standard publishing remains in the Editor header.'}
              </Text>
            </Stack>
            <CompositionBrowser
              rootVersionId={rootVersionId}
              initialReleaseId={requestedReleaseId}
              mode={guided ? 'guided' : 'advanced'}
              canEdit
            />
          </Stack>
        </Flex>
      </ScrollArea>
    </AutomationPageWrapper>
  );
}
